import { useEffect, useState } from 'react';
import {
  Order,
  OrderReturnStatus,
  OrderStatus,
  OrderWorkFlowStatus,
} from '../../../clients/quipli-service/order/order';
import { Modal } from '../../../components/modal/modal';
import { SelectForm } from '../../../components/select-form/select-form';
import { Spinner } from '../../../components/spinner/spinner';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';

export const ReopenOrder = ({
  order,
  onComplete,
  onCancel,
}: {
  order: Order;
  onComplete: () => void;
  onCancel: () => void;
}) => {
  const { reopenOrder } = useOrderApiV1();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [statuses, setStatuses] = useState<OrderWorkFlowStatus[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<OrderWorkFlowStatus>();

  const handleClose = () => {
    if (isSubmitting) return;
    onCancel();
  };

  const handleReopenOrder = async () => {
    if (!selectedStatus || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await reopenOrder(order.id, selectedStatus);
      onComplete();
    } catch (e) {
      toast(`Error moving order back to ${selectedStatus.toLocaleLowerCase()}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // set available statuses
    const statuses: OrderWorkFlowStatus[] = ['Requests'];

    if (order.status !== OrderStatus.Missed) {
      statuses.push('Approved');
    }

    if (
      order.products.some((op) => op.status === OrderReturnStatus.OffRent || op.status === OrderReturnStatus.Complete)
    )
      statuses.push('On Rent');

    setStatuses(statuses);
    statuses.length == 1 && setSelectedStatus(statuses[0]);
  }, []);

  return (
    <Modal
      isOpen={true}
      title="Revert To Previous Status"
      close={handleClose}
      additionalText={statuses.length > 1 ? `Select a status to revert the order.` : undefined}
      actions={
        isSubmitting ? (
          <Spinner />
        ) : (
          <>
            <Button className="button-block button-secondary" onClick={() => {}}>
              Cancel
            </Button>

            <Button className="button-yellow" disabled={!selectedStatus} onClick={handleReopenOrder}>
              Revert
            </Button>
          </>
        )
      }
    >
      <div>
        <SelectForm
          isRequired
          label="Status"
          isSearchable={false}
          isDisabled={isSubmitting || statuses.length === 1}
          menuPortalTarget={document.body}
          options={statuses.map((status) => ({ label: status, value: status }))}
          value={{ label: selectedStatus, value: selectedStatus }}
          onChange={(selected) => setSelectedStatus(selected?.value)}
        />
      </div>
    </Modal>
  );
};
